import type React from 'react';
import { Fragment } from 'react';

import { X } from 'lucide-react';
import { useField } from 'react-final-form';
import type { FieldArrayRenderProps } from 'react-final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';

import { Button, cn } from '#src/app/shared/components/adminShadcn';

import type { AudienceBuilderAllowedFilter } from '../../../shared/types';
import type { AudienceFilterVariant } from '../../variantStyles';
import { AUDIENCE_FILTER_VARIANT_STYLES } from '../../variantStyles';
import { LabeledSeparator } from '../LabeledSeparator';

import { FilterTypeField } from './components/FilterTypeField';
import { FilterValueField } from './components/FilterValueField';

const RemoveFilterButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
	return (
		<Button
			type="button"
			variant="outline"
			size="icon"
			aria-label="Remove filter"
			className="admin:shrink-0 admin:hover:border-destructive admin:hover:bg-destructive admin:hover:text-white"
			onClick={onClick}
		>
			<X />
		</Button>
	);
};

const OR_SLOT_BASE = 'admin:shrink-0 admin:self-center admin:text-sm admin:font-bold admin:whitespace-nowrap';

/**
 * The "+ OR" affordance shown to the right of the remove (X) button. It only
 * acts on the last alternative of a group, and only once that alternative is
 * filled in. In every other case it renders as an invisible placeholder of the
 * same width so the X button keeps its column instead of shifting to the edge.
 */
const AddOrButton: React.FC<{
	addItem: () => void;
	fieldTypeName: string;
	fieldValueName: string;
	isLast: boolean;
	className?: string;
}> = ({ addItem, fieldTypeName, fieldValueName, isLast, className }) => {
	const fieldType = useField(fieldTypeName);
	const fieldValue = useField(fieldValueName);

	const isFilled = !!(fieldType.input.value && fieldValue.input.value);

	if (!isLast || !isFilled) {
		return (
			<span aria-hidden className={cn(OR_SLOT_BASE, 'admin:invisible')}>
				+ OR
			</span>
		);
	}

	return (
		<button
			type="button"
			onClick={addItem}
			className={cn(
				OR_SLOT_BASE,
				'admin:cursor-pointer admin:bg-transparent admin:underline admin:underline-offset-4',
				className,
			)}
		>
			+ OR
		</button>
	);
};

export const OrFilterField: React.FC<{
	name: string;
	allowedFilters: readonly AudienceBuilderAllowedFilter[];
	fieldsAnd: FieldArrayRenderProps['fields'];
	index: number;
	variant: AudienceFilterVariant;
}> = ({ name, allowedFilters, fieldsAnd, index, variant }) => {
	const styles = AUDIENCE_FILTER_VARIANT_STYLES[variant];
	return (
		<>
			<FieldArray name={name} key={name}>
				{({ fields: fieldsOr }) => (
					<div className="admin:flex admin:flex-col admin:gap-2">
						{fieldsOr.map((nameOr, indexOr) => (
							<Fragment key={`${nameOr}.type`}>
								<div className="admin:flex admin:w-full admin:items-start admin:gap-2">
									<FilterTypeField
										fieldName={`${nameOr}.type`}
										fieldValueName={`${nameOr}.value`}
										allowedFilters={allowedFilters}
									/>
									<FilterValueField
										fieldName={`${nameOr}.value`}
										fieldTypeName={`${nameOr}.type`}
										allowedFilters={allowedFilters}
									/>
									<RemoveFilterButton
										onClick={() => {
											if (fieldsOr.length && fieldsOr.length > 1) {
												fieldsOr.remove(indexOr);
											} else {
												fieldsAnd.remove(index);
											}
										}}
									/>
									<AddOrButton
										addItem={() =>
											fieldsOr.push({
												value: '',
												type: '',
											})
										}
										fieldTypeName={`${nameOr}.type`}
										fieldValueName={`${nameOr}.value`}
										isLast={!!fieldsOr.length && indexOr === fieldsOr.length - 1}
										className={styles.actionLink}
									/>
								</div>
							</Fragment>
						))}
					</div>
				)}
			</FieldArray>
			{fieldsAnd.length && index !== fieldsAnd.length - 1 && (
				<LabeledSeparator
					label="AND"
					className="admin:my-2"
					labelClassName="admin:bg-slate-100"
					lineClassName={styles.line}
				/>
			)}
		</>
	);
};
